import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Send, Image, X, MessageSquare, Reply } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import useWebSocket from "../hooks/useWebSocket";
import MessageBubble from "../components/MessageBubble";
import ImageViewer from "../components/ImageViewer";
import VideoViewer from "../components/VideoViewer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PrivateChatPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [otherUser, setOtherUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [viewingImage, setViewingImage] = useState(null);
  const [viewingVideo, setViewingVideo] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const { lastMessage } = useWebSocket(user);

  useEffect(() => {
    fetchMessages();
    fetchOtherUser();
  }, [userId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (lastMessage && lastMessage.type === "new_message") {
      const msg = lastMessage.data;
      if (
        (msg.sender_id === userId && msg.recipient_id === user.user_id) ||
        (msg.sender_id === user.user_id && msg.recipient_id === userId)
      ) {
        setMessages(prev => {
          if (prev.find(m => m.message_id === msg.message_id)) return prev;
          return [...prev, msg];
        });
      }
    } else if (lastMessage && lastMessage.type === "reaction_update") {
      setMessages(prev => prev.map(m => 
        m.message_id === lastMessage.data.message_id 
          ? { ...m, reactions: lastMessage.data.reactions }
          : m
      ));
    } else if (lastMessage && lastMessage.type === "message_deleted") {
      setMessages(prev => prev.filter(m => m.message_id !== lastMessage.data.message_id));
    }
  }, [lastMessage, userId, user.user_id]);

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/messages/private/${userId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setMessages(response.data);
    } catch (error) {
      toast.error("Ошибка загрузки сообщений");
    } finally {
      setLoading(false);
    }
  };

  const fetchOtherUser = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      const foundUser = response.data.find(u => u.user_id === userId);
      setOtherUser(foundUser);
    } catch (error) {
      console.error("Error fetching user:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !selectedFile) return;

    const tempId = `temp_${Date.now()}`;
    const isVideoFile = selectedFile?.type?.startsWith("video/");
    
    // Optimistic update
    const optimisticMessage = {
      message_id: tempId,
      sender_id: user.user_id,
      sender_name: `${user.first_name} ${user.last_name}`,
      content: newMessage.trim() || (isVideoFile ? "🎬 Видео" : "📷 Фото"),
      image_url: selectedFile && !isVideoFile ? URL.createObjectURL(selectedFile) : null,
      media_url: selectedFile && isVideoFile ? URL.createObjectURL(selectedFile) : null,
      recipient_id: userId,
      reactions: {},
      created_at: new Date().toISOString(),
      is_private: true,
      reply_to: replyTo ? { sender_name: replyTo.sender_name, content: replyTo.content } : null,
      _pending: true
    };

    setMessages(prev => [...prev, optimisticMessage]);
    setNewMessage("");
    setReplyTo(null);
    const fileToUpload = selectedFile;
    setSelectedFile(null);

    try {
      const token = localStorage.getItem("token");
      let mediaUrl = null;

      if (fileToUpload) {
        setUploading(true);
        const formData = new FormData();
        formData.append("file", fileToUpload);
        
        const uploadResponse = await axios.post(`${API}/upload`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          withCredentials: true
        });
        mediaUrl = uploadResponse.data.file_url;
        setUploading(false);
      }

      const response = await axios.post(`${API}/messages`, {
        content: optimisticMessage.content,
        image_url: !isVideoFile ? mediaUrl : null,
        media_url: isVideoFile ? mediaUrl : null,
        recipient_id: userId,
        reply_to_id: replyTo?.message_id || null
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      // Replace optimistic with real
      setMessages(prev => prev.map(m => 
        m.message_id === tempId ? response.data : m
      ));
    } catch (error) {
      setMessages(prev => prev.filter(m => m.message_id !== tempId));
      toast.error("Ошибка отправки сообщения");
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 50 * 1024 * 1024) {
        toast.error("Файл слишком большой (макс. 50MB)");
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleReaction = async (messageId, emoji) => {
    // Optimistic update
    setMessages(prev => prev.map(m => {
      if (m.message_id !== messageId) return m;
      
      const reactions = { ...m.reactions };
      if (!reactions[emoji]) {
        reactions[emoji] = [];
      }
      
      if (reactions[emoji].includes(user.user_id)) {
        reactions[emoji] = reactions[emoji].filter(id => id !== user.user_id);
        if (reactions[emoji].length === 0) {
          delete reactions[emoji];
        }
      } else {
        reactions[emoji] = [...reactions[emoji], user.user_id];
      }
      
      return { ...m, reactions };
    }));

    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/messages/reaction`, {
        message_id: messageId,
        emoji: emoji
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      fetchMessages();
      toast.error("Ошибка");
    }
  };

  const handleDelete = async (messageId) => {
    setMessages(prev => prev.filter(m => m.message_id !== messageId));
    
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/messages/${messageId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
    } catch (error) {
      toast.error("Ошибка удаления");
      fetchMessages();
    }
  };

  const handleReply = (message) => {
    setReplyTo(message);
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="private-chat-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100/50 px-4 py-4 safe-area-inset-top">
        <div className="flex items-center">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(-1)}
            className="mr-2 rounded-full"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-6 h-6" />
          </Button>
          <div>
            <h1 className="font-heading text-lg font-semibold">
              {otherUser ? `${otherUser.first_name} ${otherUser.last_name}` : "Загрузка..."}
            </h1>
            {otherUser && (
              <p className="text-xs text-apple-text-tertiary capitalize">
                {otherUser.role === "coach" ? "Тренер" : 
                 otherUser.role === "athlete" ? "Спортсмен" :
                 otherUser.role === "parent" ? "Родитель" : "Любитель"}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="w-8 h-8 border-4 border-golf-green border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : messages.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">
              <MessageSquare className="w-8 h-8 text-apple-text-tertiary" />
            </div>
            <p className="text-apple-text-secondary">Начните переписку</p>
          </div>
        ) : (
          messages.map((msg) => (
            <MessageBubble
              key={msg.message_id}
              message={msg}
              isOwn={msg.sender_id === user.user_id}
              onReaction={handleReaction}
              onImageClick={setViewingImage}
              onVideoClick={setViewingVideo}
              onReply={handleReply}
              onDelete={handleDelete}
              currentUserId={user.user_id}
            />
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Reply Preview */}
      {replyTo && (
        <div className="mx-4 mb-2 p-2 bg-apple-gray rounded-xl flex items-center gap-2">
          <Reply className="w-4 h-4 text-golf-green shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-golf-green font-medium">{replyTo.sender_name}</p>
            <p className="text-sm text-gray-600 truncate">{replyTo.content}</p>
          </div>
          <button onClick={() => setReplyTo(null)} className="p-1">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>
      )}

      {/* Selected File Preview */}
      {selectedFile && (
        <div className="px-4 py-2 bg-apple-gray border-t border-gray-200">
          <div className="relative inline-block">
            {selectedFile.type?.startsWith("video/") ? (
              <video src={URL.createObjectURL(selectedFile)} className="h-20 rounded-lg" />
            ) : (
              <img src={URL.createObjectURL(selectedFile)} alt="Preview" className="h-20 rounded-lg" />
            )}
            <button
              onClick={() => setSelectedFile(null)}
              className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-gray-100 px-4 py-3 bg-white safe-area-inset-bottom">
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*,video/*"
            className="hidden"
          />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-full shrink-0"
            data-testid="attach-file-btn"
          >
            <Image className="w-5 h-5 text-golf-green" />
          </Button>
          
          <Input
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSendMessage()}
            placeholder="Сообщение..."
            className="flex-1 h-10 bg-apple-gray border-transparent rounded-full px-4"
            data-testid="message-input"
          />
          
          <Button
            onClick={handleSendMessage}
            disabled={(!newMessage.trim() && !selectedFile) || uploading}
            size="icon"
            className="rounded-full bg-golf-green hover:bg-golf-green-hover shrink-0"
            data-testid="send-btn"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Image Viewer */}
      {viewingImage && (
        <ImageViewer imageUrl={viewingImage} onClose={() => setViewingImage(null)} />
      )}
      
      {/* Video Viewer */}
      {viewingVideo && (
        <VideoViewer videoUrl={viewingVideo} onClose={() => setViewingVideo(null)} />
      )}
    </div>
  );
}
